import type { NavigateFunction } from "react-router";
import type { OpenWindowConfig } from "@/components/ui/os-layout/useWindow";
import { executeNavIntent, getAgentEntityDescriptor, resolveListHighlightHref } from "@/services/agent/agentNavigation";
import type { EntityPreviewEntityType } from "./types";

export { buildEntityFocusKey } from "@/services/agent/agentNavigation";

export type EntityListFocusDeps = {
  navigate: NavigateFunction;
  openWindow: (config: OpenWindowConfig) => void;
  inOsLayout?: boolean;
};

/** Open mission / task / case detail (not list highlight). */
export function openEntityDetail(
  entityType: EntityPreviewEntityType,
  id: string,
  { navigate, openWindow, inOsLayout = false }: EntityListFocusDeps,
): void {
  if (entityType === "artifact") {
    return;
  }
  executeNavIntent(
    { mode: "detail", entityKind: entityType, id },
    {
      inOsLayout,
      openWindow: (target) =>
        openWindow({
          appId: target.appId,
          title: target.title,
          params: target.params,
        }),
      navigate: (path, query) => {
        if (query && Object.keys(query).length > 0) {
          const search = new URLSearchParams(query).toString();
          navigate(`${path}?${search}`);
          return;
        }
        navigate(path);
      },
    },
  );
}

/** Navigate to the entity's list row and flash-highlight it without opening detail. */
export function focusEntityInList(
  entityType: EntityPreviewEntityType,
  id: string,
  { navigate, openWindow, inOsLayout = false }: EntityListFocusDeps,
): void {
  if (entityType === "artifact") {
    return;
  }
  const descriptor = getAgentEntityDescriptor(entityType);
  if (!descriptor?.listHighlight) {
    openEntityDetail(entityType, id, { navigate, openWindow, inOsLayout });
    return;
  }
  executeNavIntent(
    { mode: "list_highlight", entityKind: entityType, id },
    {
      inOsLayout,
      openWindow: (target) =>
        openWindow({
          appId: target.appId,
          title: target.title,
          params: target.params,
        }),
      navigate: (path, query) => {
        if (query && Object.keys(query).length > 0) {
          const search = new URLSearchParams(query).toString();
          navigate(`${path}?${search}`);
          return;
        }
        navigate(path);
      },
    },
  );
}

/** @deprecated Use resolveListHighlightHref from agentNavigation — kept for tests. */
export function buildEntityListFocusHref(entityType: EntityPreviewEntityType, id: string): string {
  const href = resolveListHighlightHref(entityType, id);
  if (!href) {
    return "/";
  }
  return `${href.path}${href.search}`;
}
